<template>
	<section v-if="hasAnyMedia" class="media-content-m">
		<nav
			v-if="showMediaTabs"
			class="media-content-m__tabs"
			:aria-label="t(`${I18N_NS}.sections.media`)"
		>
			<button
				v-for="group in visibleGroups"
				:key="group.key"
				type="button"
				class="media-content-m__tab"
				:class="{ 'is-active': activeType === group.key }"
				@click="switchMediaTab(group.key)"
			>
				{{ group.label }}
			</button>
		</nav>

		<div v-show="activeType === 'video'" class="media-content-m__panel">
			<section v-if="portraitVideos.length" class="shorts-grid">
				<article
					v-for="(item, index) in portraitVideos"
					:key="`portrait-${item.id}`"
					:ref="el => bindMediaImpression(el, item, index)"
					class="shorts-card"
					@click="handleToDetail(item, index)"
				>
					<div class="shorts-card__cover-wrap">
						<img class="shorts-card__cover" :src="item.cover || defaultCover" :alt="item.title" />
					</div>
					<h3 class="shorts-card__title">{{ item.title }}</h3>
					<div class="shorts-card__footer">
						<div class="shorts-card__brand">
							<img :src="brandLogo" alt="" aria-hidden="true" />
							<span>{{ brandName }}</span>
						</div>
						<div class="shorts-card__views">
							<img :src="eyeIcon" alt="" aria-hidden="true" />
							<span>{{ item.viewCount }}</span>
						</div>
					</div>
				</article>
			</section>

			<section v-if="landscapeVideos.length" class="landscape-media">
				<article
					v-for="(item, index) in landscapeVideos"
					:key="`landscape-${item.id}`"
					:ref="el => bindMediaImpression(el, item, index)"
					class="landscape-card"
					@click="handleToDetail(item, index)"
				>
					<div class="landscape-card__cover-wrap">
						<img
							class="landscape-card__cover"
							:src="item.cover || defaultCover"
							:alt="item.title"
						/>
						<span class="play-badge play-badge--corner" aria-hidden="true"></span>
						<span v-if="item.duration" class="landscape-card__duration">
							{{ item.duration }}
						</span>
					</div>
					<h3 class="landscape-card__title">{{ item.title }}</h3>
					<div class="landscape-card__meta">
						<img :src="eyeIcon" alt="" aria-hidden="true" />
						<span>{{ t(`${I18N_NS}.mediaViewCount`, { count: item.viewCount }) }}</span>
					</div>
				</article>
			</section>
		</div>

		<div v-show="activeType === 'article'" class="media-content-m__panel">
			<div v-if="articleItems.length" class="article-grid">
				<article
					v-for="(item, index) in articleItems"
					:key="`article-${item.id}`"
					:ref="el => bindMediaImpression(el, item, index)"
					class="article-card"
					@click="handleToDetail(item, index)"
				>
					<img class="article-card__cover" :src="item.cover || defaultCover" :alt="item.title" />
					<div class="article-card__shade" aria-hidden="true"></div>
					<h3 class="article-card__title">{{ item.title }}</h3>
				</article>
			</div>
		</div>
	</section>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { playContent } from "~/api/api";
import { useI18n } from "~/composables/useI18n";
import { buildOssImageUrl } from "~/utils/oss-image";
import {
	resolveMediaTypeByItem,
	useInjectedMentorDetailTracking,
} from "@variants/seer/teacher-detail/composables/useMentorDetailTracking";
import defaultCover from "~/features/page-variants/seer/square/images/video_view.png";
import eyeIcon from "~/features/page-variants/seer/square/images/eye.png";
import brandLogo from "~/assets/images/logo.png";

const I18N_NS = "teacherDetail.pc";
const { t, locale } = useI18n();
const brandName = "先知命局";
const tracking = useInjectedMentorDetailTracking();
const impressionTracker = tracking?.createImpressionTracker?.();

const props = defineProps({
	data: {
		type: Object,
		default: () => ({
			horizVideo: [],
			vertVideo: [],
			articleList: [],
		}),
	},
	profileMode: {
		type: String,
		default: "creator",
		validator: value => ["teacher", "creator"].includes(value),
	},
});

const activeType = ref("video");

const portraitVideos = computed(() => dedupeById(props.data?.vertVideo || []).map(formatVideo));
const landscapeVideos = computed(() => dedupeById(props.data?.horizVideo || []).map(formatVideo));
const articleItems = computed(() => dedupeById(props.data?.articleList || []).map(formatArticle));
const videoCount = computed(() => portraitVideos.value.length + landscapeVideos.value.length);
const hasVideoContent = computed(() => videoCount.value > 0);
const hasAnyMedia = computed(() => hasVideoContent.value || articleItems.value.length > 0);
const showMediaTabs = computed(
	() => props.profileMode === "creator" || visibleGroups.value.length > 1
);

const visibleGroups = computed(() =>
	[
		{
			key: "video",
			label: t(`${I18N_NS}.mediaVideo`),
			count: videoCount.value,
			visible: hasVideoContent.value,
		},
		{
			key: "article",
			label: t(`${I18N_NS}.mediaArticle`),
			count: articleItems.value.length,
			visible: articleItems.value.length > 0,
		},
	].filter(item => item.visible)
);

watch(
	visibleGroups,
	groups => {
		if (!groups.length) return;
		if (!groups.some(item => item.key === activeType.value)) {
			activeType.value = groups[0].key;
		}
	},
	{ immediate: true }
);

function formatVideo(item) {
	return {
		...item,
		kind: "video",
		isVideo: true,
		title: pickLocalizedTitle(item),
		cover: pickCover(item),
		duration: item.media_second_str || formatTime(item.media_second),
		viewCount: formatViewCount(item.view_count ?? item.media_play_count),
	};
}

function formatArticle(item) {
	return {
		...item,
		kind: "article",
		isVideo: false,
		title: pickLocalizedTitle(item),
		cover: pickCover(item),
		duration: "",
		viewCount: formatViewCount(item.view_count ?? item.media_play_count),
	};
}

function pickLocalizedTitle(item) {
	const isEn = locale.value === "en";
	return (isEn ? item.title_en : item.title) || item.title || "";
}

function pickCover(item) {
	const url = item.cover || item.cover_url || item.image || item.image_url || item.thumb || "";
	return buildOssImageUrl(url, { strategy: "cover", w: 640, q: 80 });
}

function formatViewCount(value) {
	const count = Number(value) || 0;
	return count.toLocaleString();
}

function formatTime(seconds) {
	const value = Number(seconds) || 0;
	if (!value) return "";
	const mins = Math.floor(value / 60)
		.toString()
		.padStart(2, "0");
	const secs = Math.floor(value % 60)
		.toString()
		.padStart(2, "0");
	return `${mins}:${secs}`;
}

function dedupeById(list) {
	const usedIds = new Set();
	return list.filter(item => {
		const id = item?.id;
		if (!id) return true;
		const normalizedId = String(id);
		if (usedIds.has(normalizedId)) return false;
		usedIds.add(normalizedId);
		return true;
	});
}

function switchMediaTab(key) {
	if (activeType.value === key) return;
	tracking?.track?.("mentor_media_tab_click", {
		media_type: key,
		content_count:
			key === "article"
				? articleItems.value.length
				: portraitVideos.value.length + landscapeVideos.value.length,
	});
	activeType.value = key;
}

function bindMediaImpression(el, item, index) {
	if (!el || !impressionTracker || !item?.id) return;
	const mediaType = resolveMediaTypeByItem(item);
	impressionTracker.bind(el, `media_${mediaType}_${item.id}`, "mentor_media_card_impression", {
		content_id: String(item.id),
		media_type: mediaType,
		position: index + 1,
	});
}

async function handleToDetail(item, index) {
	if (!item?.id) return;
	tracking?.track?.("mentor_media_card_click", {
		content_id: String(item.id),
		media_type: resolveMediaTypeByItem(item),
		position: typeof index === "number" ? index + 1 : undefined,
	});
	if (item.isVideo) {
		playContent({ square_id: item.id }).catch(() => {});
	}
	await navigateTo(`/seer/square-detail/${item.id}`);
}

onBeforeUnmount(() => {
	impressionTracker?.reset?.();
});
</script>

<style lang="scss" scoped>
@mixin line-clamp($lines: 2) {
	display: -webkit-box;
	-webkit-line-clamp: $lines;
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
	overflow-wrap: anywhere;
}

.media-content-m {
	width: 100%;
	padding: 0 32px;
	font-family: "Source Han Serif CN", "PingFang SC", sans-serif;

	&__tabs {
		display: flex;
		gap: 48px;
		margin-bottom: 24px;
	}

	&__tab {
		position: relative;
		flex: 0 0 auto;
		height: 56px;
		padding: 0;
		border: 0;
		background: transparent;
		color: #999999;
		font-size: 32px;
		font-weight: 400;
		white-space: nowrap;

		&.is-active {
			color: #333333;
			font-weight: 700;

			&::after {
				content: "";
				position: absolute;
				left: 0;
				bottom: 0;
				width: 62px;
				height: 4px;
				border-radius: 1px;
				background: #333333;
			}
		}
	}

	&__panel {
		min-width: 0;
	}
}

.shorts-grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 28px;
}

.article-grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 28px;
}

.article-card {
	position: relative;
	min-width: 0;
	aspect-ratio: 230 / 334;
	border-radius: 24px;
	overflow: hidden;
	background: #eeeeee;

	&__cover {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	&__shade {
		position: absolute;
		inset: 0;
		background: linear-gradient(
			180deg,
			rgba(0, 0, 0, 0) 0%,
			rgba(0, 0, 0, 0) 60%,
			rgba(0, 0, 0, 0.7) 100%
		);
		pointer-events: none;
	}

	&__title {
		position: absolute;
		left: 24px;
		right: 24px;
		bottom: 24px;
		margin: 0;
		color: #ffffff;
		font-size: 40px;
		font-weight: 400;
		line-height: 64px;
		@include line-clamp(2);
	}
}

.shorts-card {
	min-width: 0;

	&__cover-wrap {
		position: relative;
		aspect-ratio: 159 / 219;
		border-radius: 16px 16px 0 0;
		overflow: hidden;
		background: #eeeeee;
	}

	&__cover {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	&__title {
		margin: 12px 0 0;
		padding: 0 12px;
		color: #333333;
		font-size: 28px;
		font-weight: 700;
		line-height: 1.35;
		@include line-clamp(2);
	}

	&__footer {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;
		margin-top: 8px;
		padding: 0 12px 4px;
	}

	&__brand {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		min-width: 0;
		color: #666666;
		font-size: 28px;
		font-weight: 700;
		line-height: 36px;

		img {
			flex: 0 0 auto;
			width: 36px;
			height: 36px;
			border-radius: 50%;
			object-fit: cover;
		}

		span {
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
	}

	&__views {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		flex: 0 0 auto;
		color: #a0a0a0;
		font-size: 28px;
		line-height: 36px;

		img {
			width: 22px;
			height: 22px;
			opacity: 0.8;
		}
	}
}

.landscape-media {
	display: flex;
	flex-direction: column;
	gap: 24px;
	margin-top: 24px;
}

.landscape-card {
	min-width: 0;

	&__cover-wrap {
		position: relative;
		aspect-ratio: 343 / 193;
		border-radius: 16px;
		overflow: hidden;
		background: #eeeeee;
	}

	&__cover {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	&__duration {
		position: absolute;
		right: 8px;
		bottom: 8px;
		padding: 2px 8px;
		border-radius: 4px;
		background: rgba(0, 0, 0, 0.65);
		color: #ffffff;
		font-size: 20px;
		line-height: 1.35;
	}

	&__title {
		margin: 14px 0 8px;
		color: #333333;
		font-size: 26px;
		font-weight: 500;
		line-height: 1.45;
		@include line-clamp(1);
	}

	&__meta {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		color: #595959;
		font-family: "PingFang SC", sans-serif;
		font-size: 22px;
		line-height: 1.4;

		img {
			width: 22px;
			height: 22px;
			opacity: 0.8;
		}
	}
}

.play-badge {
	position: absolute;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	background: rgba(0, 0, 0, 0.46);
	pointer-events: none;

	&::before {
		content: "";
		width: 0;
		height: 0;
		margin-left: 3px;
		border-top: 8px solid transparent;
		border-bottom: 8px solid transparent;
		border-left: 12px solid #ffffff;
	}

	&--corner {
		top: 8px;
		right: 8px;
		width: 36px;
		height: 36px;

		&::before {
			border-top-width: 6px;
			border-bottom-width: 6px;
			border-left-width: 10px;
		}
	}
}
</style>
